import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { UserResponse } from 'stream-chat';
import { renderText as defaultRenderText, RenderTextOptions } from 'stream-chat-react';

/**
 * Escapes a leading '>' so the markdown parser renders it as the character it
 * is rather than eating it to open a blockquote.
 *
 * The rich-text composer that produces these messages deliberately cannot
 * create a blockquote, so a '>' at the start of a line is always literal prose.
 * This used to prepend a zero-width space instead, which kept the '>' visible
 * but put U+200B into the DOM and into anything copied out of a message
 * (IP-2192). A backslash escape is consumed by the parser and leaves no trace.
 *
 * @param line - The line to process
 * @returns The line with its leading '>' escaped, otherwise the original line
 */
const escapeLeadingQuote = (line: string): string => {
  if (line.startsWith('>')) {
    return '\\' + line;
  }
  return line;
};

/**
 * Custom text renderer that handles markdown formatting and workarounds for special characters.
  * We Create this because the stream-chat markdown handler is not enough to handle the markdown formatting like lists, headers, horizontal rules, etc.
  */
const CODE_FENCE_LINE = /^\s*(?:```|~~~)/;

const LIST_LINE = /^ {0,3}(?:[-*+]|\d{1,9}\.) +/;
const HEADER_LINE = /^#{1,6} +\S/;
const HORIZONTAL_RULE_LINE = /^ {0,3}(?:[-*_]{3,}) {0,3}$/;

export const customRenderText = (text?: string, mentionedUsers?: UserResponse[], options?: RenderTextOptions) => {
  if (!text) return null;

  const lines = text.split('\n');
  const hasListMarkdown = lines.some((line) => LIST_LINE.test(line));
  const hasHeaderMarkdown = lines.some((line) => HEADER_LINE.test(line));
  const hasHorizontalRule = lines.some((line) => HORIZONTAL_RULE_LINE.test(line));

  // A '>' inside a fenced code block is content, not syntax. Escaping it there
  // puts a visible backslash on screen, which is the one way the old
  // zero-width space was better (IP-2192).
  let isInCodeFence = false;
  const safeText = lines
    .map((line) => {
      if (CODE_FENCE_LINE.test(line)) {
        isInCodeFence = !isInCodeFence;
        return line;
      }
      return isInCodeFence ? line : escapeLeadingQuote(line);
    })
    .join('\n');

  const shouldUseMarkdown = hasListMarkdown || hasHeaderMarkdown || hasHorizontalRule;
  
  if (shouldUseMarkdown) {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          ul: (props) => <ul className="list-disc pl-5" {...props} />,
          ol: (props) => <ol className="list-decimal pl-5" {...props} />,
          li: (props) => <li className="mb-0.5" {...props} />,
          h1: (props) => <h1 className="text-2xl font-bold mt-4 mb-2" {...props} />,
          h2: (props) => <h2 className="text-xl font-bold mt-3 mb-2" {...props} />,
          h3: (props) => <h3 className="text-lg font-bold mt-2 mb-1" {...props} />,
          h4: (props) => <h4 className="text-base font-bold mt-2 mb-1" {...props} />,
          h5: (props) => <h5 className="text-sm font-bold mt-1 mb-1" {...props} />,
          h6: (props) => <h6 className="text-xs font-bold mt-1 mb-1" {...props} />,
          hr: (props) => <hr className="my-4 border-gray-300" {...props} />,
        }}
      >
        {safeText}
      </ReactMarkdown>
    );
  }

  return defaultRenderText(safeText, mentionedUsers, options);
};
